import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { Button, ButtonProps } from '@eventbrite/marmalade';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import escape from 'lodash/escape';
import React, { useEffect } from 'react';
import { LISTING_HEAP_IDS } from '../../../../constants';
import { useEventBasicInformationContext } from '../../../../contexts';
import { STATSIG_EVENT_NAMES } from '../../../../experimentation';
import {
    CheckoutWidgetProps,
    Event as EventProp,
    GACodes,
} from '../../../../types';
import { ConversionBarCommonProps } from '../ConversionBar/ConversionBar';
import styles from './CheckoutModalButton.module.scss';

type CheckoutModalButtonProps = CheckoutWidgetProps &
    Pick<
        ConversionBarCommonProps,
        'shouldShowStatus' | 'ticketModalButton' | 'joinWaitlist' | 'isFree'
    > &
    Pick<EventProp, 'isPreview'> & {
        handleClick?: () => void;
        style?: ButtonProps['variant'];
    } & {
        isRemindMe?: boolean;
    };

declare global {
    interface Window {
        EBWidgets?: {
            createWidget: (params: $FixMe) => void;
        };
    }
}

const generateRandomHast = () => (Math.random() + 1).toString(36).substring(7);

export const CheckoutModalButton: React.FC<CheckoutModalButtonProps> = ({
    affiliateCode,
    campaign_token,
    children,
    handleClick,
    isPreview,
    promoCode,
    rsvpToken,
    shouldShowStatus,
    style,
    ticketModalButton,
    waitlistToken,
    joinWaitlist,
    isFree,
    isRemindMe,
}) => {
    const { id: eventId, isSeriesParent } = useEventBasicInformationContext();
    const modalTriggerElementId = `eventbrite-widget-modal-trigger-${eventId}-${generateRandomHast()}`;
    const freeCTAProperties = {
        text: gettext('Reserve a spot'),
        tracking_label: 'Register',
    };
    const trackingLabel = isSeriesParent
        ? GACodes.GA_LABEL_SERIES_PARENT
        : ticketModalButton?.tracking_label;
    useEffect(() => {
        getWindowObject('EBWidgets')?.createWidget({
            widgetType: 'checkout',
            eventId,
            modal: true,
            modalTriggerElementId,
            ...(!isPreview ? { affiliateCode } : {}),
            ...(promoCode ? { promoCode: escape(promoCode) } : {}),
            ...(waitlistToken ? { waitlistToken } : {}),
            ...(rsvpToken ? { rsvpToken } : {}),
            ...(campaign_token ? { campaign_token } : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    useEffect(() => {
        if (getWindowObject('location')?.hash === '#tickets') {
            document.getElementById(modalTriggerElementId)?.click();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buttonTrackingLabel =
        isFree && !isRemindMe
            ? freeCTAProperties.tracking_label
            : ticketModalButton?.tracking_label;
    const buttonText =
        isFree && !isRemindMe
            ? freeCTAProperties.text
            : ticketModalButton?.text;

    return (
        <Button
            id={modalTriggerElementId}
            data-testid="checkout-link"
            variant={style ?? (shouldShowStatus ? 'secondary' : 'primary')}
            data-tracking-label={buttonTrackingLabel}
            className={classNames(
                {
                    'js-embed-ticket-modal-btn': !isSeriesParent,
                },
                styles.checkoutModalButton,
            )}
            size="responsive"
            onClick={() => {
                logEvent(STATSIG_EVENT_NAMES.LISTING_CONVERSION_BAR_CTA_CLICK);
                trackAnalyticsEvent({
                    action: GACodes.GA_ACTION_CHECKOUT,
                    category: GACodes.GA_CATEGORY_LISTING,
                    label: trackingLabel,
                });
                handleClick?.();
            }}
            data-heap-id={LISTING_HEAP_IDS.CHECKOUT_BUTTON}
            disabled={isPreview}
        >
            {joinWaitlist ? gettext('Join waitlist') : children ?? buttonText}
        </Button>
    );
};
